import type { AxiosResponse } from 'axios';
import type {
  VideoConfig,
  GenerationResponse,
  ApiResponse,
  AudioGenerationRequest,
  AudioGenerationResponse,
} from '../../types';
import { TIMEOUTS } from '../../constants/timings';
import { handleError } from '../../utils/errorHandler';

/**
 * Detect the backend's structured JOB_NOT_FOUND 404 from a rejected request.
 *
 * Both adapters' getGenerationStatus catch blocks read this identically: the
 * backend returns `{ success: false, data: null, error: { code: "JOB_NOT_FOUND",
 * message } }`. Returning the structured code (instead of a generic error) lets
 * the poll adapter silently drop stale job IDs from a previous backend session
 * without flashing an error banner. Returns null when the error is something else.
 */
export function detectJobNotFound(error: unknown): ApiResponse<Record<string, unknown>> | null {
  const axiosError = error as {
    response?: { status?: number; data?: { error?: { code?: string; message?: string } } };
  };
  const responseBody = axiosError.response?.data;
  if (axiosError.response?.status === 404 && responseBody?.error?.code === 'JOB_NOT_FOUND') {
    return {
      success: false,
      errorCode: 'JOB_NOT_FOUND',
      error: responseBody.error.message ?? 'Job not found',
    };
  }
  return null;
}

/**
 * Map an (already-unwrapped) job-status payload to the flat shape the poll
 * adapters consume. Superset of what FlaskLocalAdapter and ServerlessAdapter
 * each mapped before: the `?? metadata.X` fallbacks are no-ops on the local
 * (flat) store — `data.metadata` is undefined there — and the cloud-only fields
 * (`result_url`, `created_at`, `metadata`, …) are simply `undefined` locally.
 * videoPollAdapter.extractTiming reads the timing/stage fields; consumers ignore
 * any field that is absent, so the union is additive and safe for both backends.
 */
export function mapJobStatusResponse(data: Record<string, unknown>): Record<string, unknown> {
  const metadata = (data.metadata ?? {}) as Record<string, unknown>;
  return {
    job_id: data.job_id,
    status: data.status,
    progress: data.progress ?? 0,
    message: data.message,
    current_step: data.current_step ?? metadata.current_step,
    total_steps: data.total_steps ?? metadata.total_steps,
    // Prefer nested metadata.logs (serverless) then top-level data.logs (flask) —
    // matches BOTH adapters' originals exactly for their respective payloads.
    logs: metadata.logs ?? data.logs,
    video_path: data.video_path,
    result_url: data.result_url, // Single canonical result URL field (replaces video_url/audio_url)
    duration: data.duration,
    resolution: data.resolution,
    file_size: data.file_size,
    filename: data.filename,
    stats: data.stats,
    total_time: data.total_time,
    error: data.error,
    created_at: data.created_at,
    updated_at: data.updated_at,
    metadata: data.metadata,
    extra_metadata: data.extra_metadata,
    // Calibrated ETA fields from EtaEstimator. Prefer top-level (set by
    // JobStatus.to_response_dict) and fall back to metadata for stores that nest them.
    started_at: data.started_at ?? metadata.started_at,
    completed_at: data.completed_at ?? metadata.completed_at,
    elapsed_seconds: data.elapsed_seconds ?? metadata.elapsed_seconds,
    estimated_remaining_seconds:
      data.estimated_remaining_seconds ?? metadata.estimated_remaining_seconds,
    estimated_total_seconds: data.estimated_total_seconds ?? metadata.estimated_total_seconds,
    current_stage: data.current_stage ?? metadata.current_stage,
    current_stage_label: data.current_stage_label ?? metadata.current_stage_label,
  };
}

/**
 * Base interface for API adapters
 * This allows us to have different implementations for Flask local and Serverless APIs
 */
export abstract class BaseAPIAdapter {
  protected baseURL: string;
  protected timeout: number;

  /** Subclass label used in error logging (e.g. 'FlaskLocalAdapter'). */
  protected abstract readonly adapterName: string;

  constructor(baseURL: string, timeout: number = TIMEOUTS.GENERATION_API) {
    // Use shared timeout configuration
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  /**
   * Run a request whose result is the raw (already-unwrapped) response body and
   * wrap it in the standard `{ success, data } | { success: false, error }`
   * envelope shared by every passthrough method.
   *
   * `call` is a thunk so each subclass keeps its EXACT request (path, timeout,
   * retry/cold-start wrapper, warmup side-effects) — only the envelope + error
   * handling are centralized here.
   */
  protected async wrapPassthrough<T>(
    call: () => Promise<AxiosResponse>,
    errorContext: string
  ): Promise<ApiResponse<T>> {
    try {
      const response = await call();
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: handleError(error, this.adapterName, errorContext),
      };
    }
  }

  /**
   * Unwrap the raw axios `response.data` into the actual job payload.
   *
   * Default (FlaskLocalAdapter): identity — its response interceptor already
   * stripped the `{ success, data, error }` envelope. ServerlessAdapter overrides
   * this to peel the envelope inline (`raw.data ?? raw`).
   */
  protected unwrapResponse(raw: unknown): Record<string, unknown> {
    return (raw ?? {}) as Record<string, unknown>;
  }

  /**
   * Fetch + map a job-status response, shared by both adapters' getGenerationStatus.
   * `doGet` is a thunk so each subclass keeps its own retry policy; the unwrap is a
   * hook (interceptor-stripped vs inline) and the JOB_NOT_FOUND 404 + error envelope
   * are handled here once.
   */
  protected async fetchJobStatus(
    doGet: () => Promise<AxiosResponse>,
    errorContext: string
  ): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const response = await doGet();
      return {
        success: true,
        data: mapJobStatusResponse(this.unwrapResponse(response.data)),
      };
    } catch (error: unknown) {
      const notFound = detectJobNotFound(error);
      if (notFound) return notFound;
      return {
        success: false,
        error: handleError(error, this.adapterName, errorContext),
      };
    }
  }

  /**
   * Check if the backend service is available
   */
  abstract checkHealth(): Promise<ApiResponse<{ status: string; version?: string }>>;

  /**
   * Generate a video from configuration
   */
  abstract generateVideo(config: VideoConfig): Promise<GenerationResponse>;

  /**
   * Get the status of a generation job
   */
  abstract getGenerationStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>>;

  /**
   * Generate audio from configuration
   */
  abstract generateAudio(config: AudioGenerationRequest): Promise<AudioGenerationResponse>;

  /**
   * Get the status of an audio generation job
   */
  abstract getAudioStatus(generationId: string): Promise<ApiResponse<Record<string, unknown>>>;
}
